import React, { forwardRef } from 'react';

import { Chip as MuiChip, ChipProps as MuiChipProps } from '@mui/material';

import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';

import styles from './Chip.module.scss';

export type ChipSize = 'default' | 'small';
export type ChipType = 'default' | 'filter' | 'project';

export interface ChipProps extends Omit<MuiChipProps, 'size' | 'variant' | 'color'> {
    /**
     * The label text of the chip
     */
    label: string;
    /**
     * The size of the chip
     */
    size?: ChipSize;
    /**
     * The type/variant of the chip
     */
    type?: ChipType;
    /**
     * If true, shows a delete icon
     */
    onDelete?: () => void;
    /**
     * Custom icon to display (for project type)
     */
    icon?: React.ReactElement;
}

/**
 * Custom Chip component based on MUI Chip
 * Implements design system styling from Figma atoms Tag component
 * Supports light and dark themes via CSS variables
 */
export const Chip = forwardRef<HTMLDivElement, ChipProps>(
    (
        { label, size = 'default', type = 'default', onDelete, icon, disabled = false, className = '', ...props },
        ref,
    ) => {
        const chipClass = [styles.chip, styles[size], styles[type], className].filter(Boolean).join(' ');

        return (
            <MuiChip
                label={label}
                className={chipClass}
                disabled={disabled}
                onDelete={onDelete}
                deleteIcon={onDelete ? <MuiIcon icon={'close_small'} /> : undefined}
                icon={type === 'project' && icon ? icon : undefined}
                ref={ref}
                {...props}
            />
        );
    },
);

export default Chip;
